import React from "react"
import { FieldProps, FieldRenderProps } from "react-final-form"

import { FlexRow, AbsoluteBox, RelativeBox, BoxProps } from "components/box"
import { EyebrowSmall } from "components/text"
import Icon from "components/icon"

import { Option } from "typings/select-options"
import { SaveResponses } from "typings/quiz"

import * as Input from "./styles"

export const FIELD_SUBSCRIPTION = {
  value: true,
  error: true,
  submitFailed: true,
  submitError: true,
  touched: true,
  dirtySinceLastSubmit: true
}

/* This is the type for the props of the component inside the DefaultField */
type InputComponentProps<T> = React.HTMLProps<HTMLInputElement> & {
  options?: Option<T>[]
  handleResetError?: () => void
  error: boolean
  dataTest?: string
} & SaveResponses

export type DefaultFieldProps<T> = FieldProps<T, HTMLInputElement> &
  FieldRenderProps<T, HTMLInputElement> &
  BoxProps & {
    InputComponent: React.ComponentType<InputComponentProps<T>>
    customProps: {
      options?: Option<T>[]
      onChange?: (e: React.FormEvent<HTMLInputElement>) => void
      isSearchable?: boolean
    }
    dataTest?: string
    handleSetError?: () => void
    handleResetError?: () => void
  }

export function DefaultField<ValueType extends { toString: () => string }>({
  input,
  meta,
  id,
  disabled,
  customProps = {},
  InputComponent,
  label,
  hint,
  defaultValue: _d,
  placeholder,
  autoComplete,
  pattern,
  dataTest,
  handleSetError,
  handleResetError,
  saveResponses,
  error: customError,
  ...styledProps
}: DefaultFieldProps<ValueType>) {
  const { active, error, submitFailed, submitError, touched, dirtySinceLastSubmit } = meta
  let displayError = undefined

  if (submitFailed || touched) {
    // only show errors if we've entered/left the field or submit has failed

    if (error) {
      // Default to field specific/presubmit errors
      displayError = error
      !customError && handleSetError && handleSetError()
    } else if (!dirtySinceLastSubmit) {
      // show submit errors as long as the field hasn't been altered
      displayError = submitError
    }
  }

  return (
    <RelativeBox>
      <Input.Box active={!disabled && active} disabled={disabled} error={displayError} {...styledProps}>
        <FlexRow justifyContent="space-between" flexWrap="wrap">
          <InputComponent
            id={id || input.name}
            className="input-component" /* className is necessary because styled props cannot be passed to lib components */
            disabled={disabled}
            placeholder={placeholder || " "}
            autoComplete={autoComplete}
            pattern={pattern}
            saveResponses={saveResponses}
            {...customProps}
            {...input}
            value={input.value.toString()}
            onChange={e => {
              /* If custom onChange handler is passed we chain it to the default onChange handler */
              input.onChange(e)
              customProps.onChange && customProps.onChange(e)
            }}
            handleResetError={handleResetError}
            error={customError}
            data-test={dataTest}
            dataTest={dataTest}
          />
          <EyebrowSmall as="label" htmlFor={input.name}>
            {label}
          </EyebrowSmall>
          {displayError && (
            <AbsoluteBox top={21} right={15}>
              <Icon name="error" />
            </AbsoluteBox>
          )}
        </FlexRow>
      </Input.Box>
      {hint && <Input.Hint>{hint}</Input.Hint>}
      {displayError && <Input.Error>{displayError}</Input.Error>}
    </RelativeBox>
  )
}
